import React from 'react'
import {MAIN_IMAGES_IMAGE_TYPE} from '../constants'
import {findImageGroupBy} from '../utils/image-groups-utils'
import {isServer} from '../utils/utils'
import useMultiSite from './use-multi-site'
import {categoryUrlBuilder} from '../utils/url'
import useOcapiSite from '../commerce-api/hooks/useOcapiSite'
import {absoluteUrl} from '../utils/url'
import {useLocation} from 'react-router-dom'

const useStructuredData = () => {
    const context = 'https://www.schema.org'
    const {buildUrl} = useMultiSite()
    const {logo} = useOcapiSite()
    const pathname = useLocation().pathname
    const validValuesForAvailabilityField = [
        'InStock',
        'LowStock',
        'OutOfStock',
        'LimitedAvailability',
        'OnlineOnly',
        'PreOrder',
        'SoldOut',
        'Discontinued'
    ]
    const getAvailability = (product) => {
        const productAvailability = product?.c_availability
        if (!productAvailability) {
            return null
        }
        const availability =
            validValuesForAvailabilityField.find(
                (value) => productAvailability?.toLowerCase() === value?.toLowerCase()
            ) || null
        return availability
    }
    const getProductImage = (product) => {
        const imageGroups = product?.imageGroups || []
        const selectedVariationAttributes = product?.variationValues || {}
        const productImagesArr = findImageGroupBy(imageGroups, {
            viewType: MAIN_IMAGES_IMAGE_TYPE,
            selectedVariationAttributes
        })?.images
        return productImagesArr?.[0]?.disBaseLink || productImagesArr?.[0]?.link
    }

    const getBreadcrumbItem = (position, name, url) => {
        return {
            '@type': 'ListItem',
            position: position,
            name: name,
            item: absoluteUrl(url)
        }
    }

    const getProductPageJSONLDScript = (product, categoryName = 'thing') => {
        if (!product) {
            return null
        }
        const type = product?.['product-type']?.toLowerCase?.() || 'product'
        const id = product?.id
        const brand = product?.brand || 'Stokke'
        const name = product?.name
        const image = getProductImage(product)
        const description = product?.pageDescription
        const availability = getAvailability(product)
        const price = product?.price?.toFixed?.(2)
        const priceCurrency = product?.currency
        const url = product?.c_canonicalUrl || product?.slugUrl || product?.c_slugUrl
        const dataObject = {
            '@context': context,
            '@type': type,
            sku: id,
            brand: {
                '@type': 'Brand',
                name: brand,
                logo
            },
            category: categoryName,
            ...(name && {name}),
            ...(description && {description}),
            ...(image && {image}),
            offers: {
                '@type': 'Offer',
                ...(price && {price}),
                ...(priceCurrency && {priceCurrency}),
                ...(availability && {availability}),
                ...(url && {url})
            }
        }
        return <script type="application/ld+json">{JSON.stringify(dataObject)}</script>
    }
    const getBreadcrumbListJSONLDScript = (primaryCategory, product) => {
        if (!primaryCategory) {
            return null
        }
        const host = !isServer && window?.location?.host
        const itemsArr = []
        const dataObject = {
            '@context': context,
            '@type': 'BreadcrumbList'
        }

        // Skip the first element in the array as it is the root
        // category(allproducts) and we don't want to display it
        for (let i = 1; i < primaryCategory?.parentCategoryTree?.length; i++) {
            const category = primaryCategory?.parentCategoryTree?.[i]
            const categoryUrl = buildUrl(categoryUrlBuilder(category))
            itemsArr.push(
                getBreadcrumbItem(
                    i,
                    category?.name,
                    host ? `https://${host}${categoryUrl}` : `${categoryUrl}`
                )
            )
        }

        // Add the product itself as the last element in the array
        if (product) {
            itemsArr.push(getBreadcrumbItem(itemsArr.length + 1, product?.name, pathname))
        }

        dataObject.itemListElement = itemsArr
        const resultString = itemsArr.length ? JSON.stringify(dataObject) : null
        return itemsArr.length ? <script type="application/ld+json">{resultString}</script> : null
    }
    return {
        getProductPageJSONLDScript,
        getBreadcrumbListJSONLDScript
    }
}
export default useStructuredData
